package com.arena_web.ui.components.auth

import androidx.compose.runtime.*
import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.core.viewmodel.LoginViewModel
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.utils.*
import com.arena_web.utils.OAuthHelper
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

/**
 * Figma 10093:238687 / 10093:238709 — primary OAuth sign-in pill. Used in the
 * header, AccountRequiredModal, and WelcomeToArenaScreen so the visual stays
 * consistent across every guest-facing entry point.
 *
 * The button owns its own [LoginViewModel]; callers just supply [onLoggedIn]
 * which fires after the access token has been stored and authenticated
 * clients have been created in the container.
 */
@Composable
fun GoogleSignInButton(
    label: String = "Sign in with Google",
    /** true → pill stretches to fill its parent's width with centred content
     *  (mobile stacked layout). false → content-width pill (desktop). */
    fullWidth: Boolean = false,
    onLoggedIn: () -> Unit,
) {
    val container = LocalAppContainer.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container,
        )
    }

    OAuthPillButton(
        iconSrc = AppIcons.Google,
        iconSize = 22,
        label = label,
        fullWidth = fullWidth,
        onClick = {
            AnalyticsTracker.trackClick("login", params = mapOf("provider" to "google"))
            OAuthHelper.triggerGoogleSignIn(
                onSuccess = { googleUser ->
                    val lang = OAuthHelper.localeToLanguageName(googleUser.locale)
                    loginViewModel.loginWithOAuth(
                        provider = "google",
                        providerId = googleUser.sub,
                        email = googleUser.email,
                        fullName = googleUser.name,
                        avatarUrl = googleUser.picture,
                        languages = lang?.let { listOf(it) },
                        onSuccess = onLoggedIn,
                    )
                },
                onError = { error -> console.error("Google sign-in error: $error") },
            )
        },
    )
}

@Composable
fun AppleSignInButton(
    label: String = "Sign in with Apple",
    /** true → pill stretches to fill its parent's width with centred content
     *  (mobile stacked layout). false → content-width pill (desktop). */
    fullWidth: Boolean = false,
    onLoggedIn: () -> Unit,
) {
    val container = LocalAppContainer.current
    val loginViewModel = remember {
        LoginViewModel(
            oauthLoginUseCase = container.getOAuthLoginUseCase(),
            container = container,
        )
    }

    OAuthPillButton(
        iconSrc = AppIcons.Apple,
        iconSize = 24,
        label = label,
        fullWidth = fullWidth,
        onClick = {
            AnalyticsTracker.trackClick("login", params = mapOf("provider" to "apple"))
            OAuthHelper.signInWithApple(
                onSuccess = { email, name, providerId ->
                    loginViewModel.loginWithOAuth(
                        provider = "apple",
                        providerId = providerId,
                        email = email,
                        fullName = name,
                        onSuccess = onLoggedIn,
                    )
                },
                onError = { error -> console.error("Apple sign-in error: $error") },
            )
        },
    )
}

/**
 * Pill shell shared by both providers. The button label stays put across the
 * whole OAuth round-trip — we don't swap to a "Signing in…" state because
 * Google Identity Services fires no callback when the user dismisses the
 * account chooser, which would otherwise leave the button stuck. A 600 ms
 * debounce on the click handler swallows accidental double-clicks while still
 * letting a deliberate retry through.
 */
@Composable
private fun OAuthPillButton(
    iconSrc: String,
    iconSize: Number,
    label: String,
    fullWidth: Boolean = false,
    onClick: () -> Unit,
) {
    var lastClickAt by remember { mutableStateOf(0.0) }

    Row(
        modifier = Modifier
            .then(if (fullWidth) Modifier.fillMaxWidth() else Modifier)
            .sHeight(48)
            .sBorderRadius(26)
            .sPadding(8, 18)
            .sGap(12)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, #09142D, #09142D) padding-box, " +
                        "${AppGradients.ButtonBorderGradientPrimary} border-box",
                )
            }
            .onClick {
                val now = kotlin.js.Date.now()
                if (now - lastClickAt < 600) return@onClick
                lastClickAt = now
                onClick()
            },
        horizontalArrangement = if (fullWidth) Arrangement.Center else Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Icon(src = iconSrc, modifier = Modifier.sSize(iconSize))
        SpanText(
            label,
            modifier = Modifier
                .bodyLMedium()
                .color(AppColors.Text.Secondary)
                .textAlign(TextAlign.Center),
        )
    }
}
